import { PropertyType } from '@beatflyer/shaders-lib';
import { CompositionData } from 'data/FlyerData';
import Layer from '../data/Layer';
import { newUUID } from 'utils';

export const NO_DURATION = 'Instant';
export const DURATION_SHORT = '0.15 seconds';
export const DURATION_NORMAL = '0.3 seconds';
export const DURATION_LONG = '0.6 seconds';
export const DURATION_VERY_LONG = '1 second';
export const DURATION_VERY_VERY_LONG = '2 seconds';
export const DURATION_SUPER_LONG = '5 seconds';

export const DIRECTION_HORIZONTAL = 'horizontal';
export const DIRECTION_HORIZONTAL_INVERTED = 'horizontal inverted';
export const DIRECTION_VERTICAL = 'vertical';
export const DIRECTION_VERTICAL_INVERTED = 'vertical inverted';

const { Power3, Elastic } = window as any;

export const EASING_BEZIER_OUT_CUBIC = Power3.easeOut;
export const EASING_BEZIER_IN_CUBIC = Power3.easeIn;

export const EASING_BEZIER_OUT_BOUNCE = Elastic.easeOut.config(1, 0.3);
export const EASING_BEZIER_IN_BOUNCE = Elastic.easeIn.config(1, 0.75);

export class CompositionEffect {
  id: string;
  layer: Layer;
  visible: boolean = true;
  isText: boolean = false;

  isPreviewing: boolean = false;
  isTransitioning: boolean = false;
  lastRenderedFrame = -1;

  static createCompositionEffect(
    layer: Layer,
    id: string,
    data?: CompositionData
  ): CompositionEffect {
    const clazz = (window as any).BeatFlyer.compositions[id];
    return new clazz(layer, data);
  }

  constructor(layer: Layer, data: any) {
    this.id = newUUID();
    this.layer = layer;
    this.initialize(data);
  }

  initialize(data: CompositionData) {
    this.visible = true;

    const properties = this.getPropertiesDescriptors();
    for (let key in properties) {
      const p = properties[key];
      this.setProperty(key, p.initialValue);
    }
    if (data && data.props) {
      if (data.visible !== undefined) {
        this.visible = data.visible;
      }
      for (let key in data.props) {
        this.setProperty(key, data.props[key]);
      }
    }
  }

  protected getInitialDuration(): string {
    return DURATION_NORMAL;
  }

  protected getDurationOptions(): string[] {
    return [
      NO_DURATION,
      DURATION_SHORT,
      DURATION_NORMAL,
      DURATION_LONG,
      DURATION_VERY_LONG
    ];
  }

  getPropertiesDescriptors() {
    const initialValue = this.getInitialDuration();
    const options = this.getDurationOptions();
    return {
      inDuration: {
        type: PropertyType.SELECT,
        label: 'Duration In',
        options,
        initialValue
      },
      sameOut: {
        label: 'Same Out',
        type: PropertyType.BOOLEAN,
        initialValue: true
      },
      outDuration: {
        type: PropertyType.SELECT,
        label: 'Duration Out',
        options,
        initialValue,
        shouldBeVisible: () => {
          const sameOut = this.getProperty('sameOut');
          return sameOut === false;
        }
      }
    };
  }

  setProperty(key: string, value: any) {
    this['__' + key] = value;
  }
  getProperty(key: string, mute: boolean = false): any {
    const result = this['__' + key];
    if (result === undefined && !mute) {
      console.log('Could not find property "' + key + '" in composition');
    }
    return result;
  }

  hasInitialValues() {
    if (!this.visible) {
      return false;
    }
    const properties = this.getPropertiesDescriptors();
    for (let key in properties) {
      if (this.getProperty(key) !== properties[key].initialValue) {
        return false;
      }
    }
    return true;
  }

  restoreInitialValues() {
    this.visible = true;
    const properties = this.getPropertiesDescriptors();
    for (let key in properties) {
      const p = properties[key];
      this.setProperty(key, p.initialValue);
    }
  }

  update(time: number, duration: number) {
    if (!this.visible) {
      this.isTransitioning = false;
      return;
    }
    // check stage in relation to frame
    // console.log('FX: ' + time + ' - ' + duration);
    let start = this.layer.getTimelineStart();
    let end = this.layer.getTimelineEnd();
    const layerDuration = end - start;
    // console.log('start: ' + start + ' end: ' + end);
    start *= duration;
    end *= duration;
    let isTransitioning = true;
    let inDuration = parseFloat(this.getProperty('inDuration'));
    if (isNaN(inDuration)) {
      inDuration = 0;
    }
    const sameOut = this.getProperty('sameOut') as boolean;
    let outDuration = sameOut
      ? inDuration
      : parseFloat(this.getProperty('outDuration'));
    if (isNaN(outDuration)) {
      outDuration = 0;
    }

    if (time <= start) {
      this.compose(0);
    } else if (time >= end) {
      this.decompose(1);
    } else if (time < start + inDuration) {
      this.compose((time - start) / inDuration);
    } else if (time > end - outDuration) {
      this.decompose((time - (end - outDuration)) / outDuration);
    } else {
      if (this.layer.frames.length > 1) {
        // compute intermediates transitions
        // get current visible frame
        const { visibleFrameIndex } = this.layer;
        const frame = this.layer.frames[visibleFrameIndex];
        let frameStart = start;
        for (let i = 0; i < visibleFrameIndex; i++) {
          const f = this.layer.frames[i];
          frameStart += f.relativeDuration * layerDuration * duration;
        }
        const frameEnd =
          frameStart + frame.relativeDuration * layerDuration * duration;
        // console.log(
        //   `Frame ${visibleFrameIndex} - start: ${frameStart} - end: ${frameEnd}`
        // );
        if (frameEnd - frameStart < inDuration + outDuration) {
          // TODO: this should somehow only apply once
          if (
            this.isTransitioning ||
            visibleFrameIndex !== this.lastRenderedFrame
          ) {
            this.compose(1);
          }
          isTransitioning = false;
        } else if (time < frameStart + inDuration) {
          this.compose((time - frameStart) / inDuration);
        } else if (time > frameEnd - outDuration) {
          this.decompose((time - (frameEnd - outDuration)) / outDuration);
        } else {
          // TODO: this should somehow only apply once
          if (
            this.isTransitioning ||
            visibleFrameIndex !== this.lastRenderedFrame
          ) {
            this.compose(1);
          }
          isTransitioning = false;
        }
        this.lastRenderedFrame = visibleFrameIndex;
      } else {
        // TODO: this should somehow only apply once
        if (this.isTransitioning) {
          this.compose(1);
        }
        isTransitioning = false;
      }
    }
    this.isTransitioning = isTransitioning;
  }

  protected compose(position: number) {
    // to override
  }

  protected decompose(position: number) {
    // to override
  }

  reset() {
    this.isTransitioning = false;
    this.layer.resetGeomTransformation();
  }

  toObject(): CompositionData {
    const props = {};
    const properties = this.getPropertiesDescriptors();
    for (let key in properties) {
      props[key] = this.getProperty(key);
    }
    const result: CompositionData = {
      id: Object.getPrototypeOf(this).constructor.id,
      props
    } as any;
    if (!this.visible) {
      result.visible = false;
    }
    return result;
  }
}



// WEBPACK FOOTER //
// ./src/compose/CompositionEffect.ts